import { SimWorld, addLog, recordDuelResult, takeDailySnapshot, isGroupModeEnabled, isSameGroup, isDifferentGroup, isAsymmetricEvolutionEnabled, getBattleConfigForNpc } from '../types/SimWorld';
import { SimNPC } from '../types/SimNPC';
import { GameMap } from '../types/GameMap';
import { getGroupBattleConfig } from '../types/Group';
import { simulateGame } from '../../engine/gameEngine/Simulator';
import { updateRelationship } from '../systems/RelationshipManager';
import { processPartnerFormation } from '../systems/FamilyManager';
import { processBirths } from '../systems/BirthManager';
import { processDeaths, processCulling } from '../systems/DeathManager';
import { processImmigration } from '../systems/ImmigrationManager';
import { moveNPC } from '../map/MovementManager';
import { applyTileEffect } from '../map/TileEffects';
import { checkLocationInteractions } from '../map/LocationInteraction';
import { shuffle } from '../../utils/helpers';
import type { ExtendedCardData } from '../../utils/cardManager';
import type { CardWithId, MonsterInstance } from '../../types/card';
import { POPULATION_CONSTANTS } from './PopulationConstants';

// ゲームバランス定数
const ELO_K_FACTOR = 32; // Eloレーティング K値
const RECENT_OPPONENT_DAYS = 5; // 直近対戦履歴の保持日数

// マッチング設定
const MATCHING_CONFIG = {
  ENABLE_POSITION_WEIGHT: true, // 位置による重み付けを有効にするか
  RECENT_OPPONENT_WEIGHT: 0.1, // 直近対戦相手の重み（低いほど避ける）
  ADJACENT_POSITION_WEIGHT: 2.0, // 隣接タイルの重み（高いほど優先）
};

/**
 * 1日分のシミュレーション
 * @param allCards 全カードデータ（出生時のデッキ生成に使用）
 * @param map ゲームマップ（Phase 2で追加）
 */
export function simulateDay(
  world: SimWorld,
  allCards: ExtendedCardData[],
  map: GameMap
): void {
  const { npcs, day } = world;

  addLog(world, `=== Day ${day + 1} 開始 ===`);

  // === 午前：移動（Phase 2で追加） ===
  const alive = npcs.filter((n) => n.isAlive);
  for (const npc of alive) {
    moveNPC(npc, map, alive);
  }

  // === 昼：デュエル（移動後の位置で） ===
  if (isAsymmetricEvolutionEnabled(world)) {
    // 非対称進化モード: 内部戦闘と外部戦闘を分離
    executeAsymmetricDuels(alive, world);
  } else if (isGroupModeEnabled(world)) {
    // 通常グループモード: 別グループ同士でのみ対戦
    const duelPairs = scheduleDuelsGroupMode(alive, world);
    for (const { npc1, npc2 } of duelPairs) {
      executeDuel(world, npc1, npc2, 'external');
    }
  } else {
    // 通常モード: 従来通りのペアリング
    const duelPairs = scheduleDuels(alive, world);
    for (const { npc1, npc2 } of duelPairs) {
      executeDuel(world, npc1, npc2, 'normal');
    }
  }

  // === 午後：場所ベース交流（Phase 2で追加） ===
  checkLocationInteractions(map, world);

  // === 夕方：タイル効果（Phase 2で追加） ===
  // 水場での回復、属性ゾーンでの突然変異など
  for (const npc of alive) {
    const tile = map.tiles[npc.position.y][npc.position.x];
    applyTileEffect(npc, tile, world, allCards);
  }

  // === 夜：追加交流（Phase 1のランダム交流） ===
  // 場所ベース交流に加えて、ランダム交流も継続
  processPositiveInteractions(world);

  // === 深夜：パートナー形成、出生判定 ===
  processPartnerFormation(world);

  // 最大人口計算（マップサイズ依存）
  const maxPopulation = Math.floor(
    map.width * map.height * POPULATION_CONSTANTS.MAX_POPULATION_RATIO
  );
  processBirths(world, allCards, maxPopulation);

  // === 深夜：寿命チェック ===
  processDeaths(world, allCards);

  // === 深夜：デュエル淘汰チェック ===
  processCulling(world, allCards);

  // === 深夜：移住イベント ===
  processImmigration(world, allCards, map);

  // === 深夜：年齢更新 ===
  for (const npc of npcs) {
    if (npc.isAlive) {
      npc.age++;
      npc.daysLived++;
    }
  }

  world.day++;

  // === 日別スナップショット保存 ===
  takeDailySnapshot(world);
}

/**
 * デュエルペア型
 */
interface DuelPair {
  npc1: SimNPC;
  npc2: SimNPC;
}

/**
 * Eloレーティング計算
 * @param winnerRating 勝者の現在レーティング
 * @param loserRating 敗者の現在レーティング
 * @returns 新しいレーティング
 */
function calculateEloRating(
  winnerRating: number,
  loserRating: number
): { winnerNewRating: number; loserNewRating: number } {
  // 期待勝率を計算
  const expectedWinner = 1 / (1 + Math.pow(10, (loserRating - winnerRating) / 400));
  const expectedLoser = 1 / (1 + Math.pow(10, (winnerRating - loserRating) / 400));

  // 新しいレーティングを計算
  const winnerNewRating = Math.round(winnerRating + ELO_K_FACTOR * (1 - expectedWinner));
  const loserNewRating = Math.round(loserRating + ELO_K_FACTOR * (0 - expectedLoser));

  return { winnerNewRating, loserNewRating };
}

/**
 * 直近対戦相手リストを更新
 * @param npc 更新対象のNPC
 * @param opponentId 対戦相手のID
 */
function updateRecentOpponents(npc: SimNPC, opponentId: string): void {
  // 既に含まれている場合は削除（重複防止）
  const existingIndex = npc.recentOpponents.indexOf(opponentId);
  if (existingIndex !== -1) {
    npc.recentOpponents.splice(existingIndex, 1);
  }

  // 新しい対戦相手を追加
  npc.recentOpponents.push(opponentId);

  // N日分を超えたら古いものを削除
  while (npc.recentOpponents.length > RECENT_OPPONENT_DAYS) {
    npc.recentOpponents.shift();
  }
}

/**
 * 2点間のマンハッタン距離を計算
 */
function getManhattanDistance(pos1: { x: number; y: number }, pos2: { x: number; y: number }): number {
  return Math.abs(pos1.x - pos2.x) + Math.abs(pos1.y - pos2.y);
}

/**
 * 対戦相手の重みを計算
 * @param npc 自分
 * @param candidate 対戦相手候補
 * @returns 重み（高いほど選ばれやすい）
 */
function calculateOpponentWeight(npc: SimNPC, candidate: SimNPC): number {
  let weight = 1.0;

  // 直近で対戦した相手は重みを下げる
  if (npc.recentOpponents.includes(candidate.id)) {
    weight *= MATCHING_CONFIG.RECENT_OPPONENT_WEIGHT;
  }

  // 位置による重み付け（オプション）
  if (MATCHING_CONFIG.ENABLE_POSITION_WEIGHT) {
    const distance = getManhattanDistance(npc.position, candidate.position);
    if (distance <= 1) {
      // 隣接タイル（距離0-1）は重みアップ
      weight *= MATCHING_CONFIG.ADJACENT_POSITION_WEIGHT;
    }
  }

  return weight;
}

/**
 * 重み付きランダム選択
 * @param candidates 候補リスト
 * @param weights 各候補の重み
 * @returns 選択されたインデックス
 */
function weightedRandomSelect(weights: number[]): number {
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight === 0) {
    return Math.floor(Math.random() * weights.length);
  }

  let random = Math.random() * totalWeight;
  for (let i = 0; i < weights.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return i;
    }
  }
  return weights.length - 1;
}

/**
 * デュエルスケジューリング（重み付きペアリング）
 * グループモードの場合は別グループ同士でのみ対戦
 */
function scheduleDuels(npcs: SimNPC[], world: SimWorld): DuelPair[] {
  const pairs: DuelPair[] = [];
  const groupModeEnabled = isGroupModeEnabled(world);

  if (groupModeEnabled) {
    // グループモード: 別グループ同士でペアリング
    return scheduleDuelsGroupMode(npcs, world);
  }

  // 通常モード: 従来通りのペアリング
  const available = [...npcs];

  while (available.length >= 2) {
    // 1人目はランダムに選択
    const idx1 = Math.floor(Math.random() * available.length);
    const npc1 = available.splice(idx1, 1)[0];

    // 2人目は重み付きで選択
    const weights = available.map(candidate => calculateOpponentWeight(npc1, candidate));
    const idx2 = weightedRandomSelect(weights);
    const npc2 = available.splice(idx2, 1)[0];

    pairs.push({ npc1, npc2 });
  }

  // 奇数の場合、1人あまる
  return pairs;
}

/**
 * グループモード用デュエルスケジューリング
 * 別グループ同士でのみ対戦
 */
function scheduleDuelsGroupMode(npcs: SimNPC[], world: SimWorld): DuelPair[] {
  const pairs: DuelPair[] = [];
  const matched = new Set<string>(); // マッチ済みNPCのID

  // グループ別にNPCを分類
  const groupNpcs = new Map<string, SimNPC[]>();
  for (const npc of npcs) {
    if (!npc.group) continue;
    if (!groupNpcs.has(npc.group)) {
      groupNpcs.set(npc.group, []);
    }
    groupNpcs.get(npc.group)!.push(npc);
  }

  const groupIds = Array.from(groupNpcs.keys());
  if (groupIds.length < 2) return pairs; // グループが1つ以下なら対戦なし

  // 各グループからランダムに選出して対戦
  // ラウンドロビン方式: グループAとB、BとC、CとA...
  for (let round = 0; round < 3; round++) { // 最大3ラウンド
    for (let i = 0; i < groupIds.length; i++) {
      const groupA = groupIds[i];
      const groupB = groupIds[(i + 1 + round) % groupIds.length];
      if (groupA === groupB) continue;

      const npcListA = groupNpcs.get(groupA)!.filter(n => !matched.has(n.id));
      const npcListB = groupNpcs.get(groupB)!.filter(n => !matched.has(n.id));

      // ペア数は少ない方に合わせる
      const pairCount = Math.min(npcListA.length, npcListB.length);

      // シャッフルしてペアリング
      const shuffledA = [...npcListA].sort(() => Math.random() - 0.5);
      const shuffledB = [...npcListB].sort(() => Math.random() - 0.5);

      for (let j = 0; j < pairCount; j++) {
        const npc1 = shuffledA[j];
        const npc2 = shuffledB[j];
        pairs.push({ npc1, npc2 });
        matched.add(npc1.id);
        matched.add(npc2.id);
      }
    }
  }

  return pairs;
}

/**
 * 非対称進化モード用デュエル実行
 * 内部戦闘と外部戦闘を分けて実行
 */
function executeAsymmetricDuels(npcs: SimNPC[], world: SimWorld): void {
  // グループ別にNPCを分類
  const groupNpcs = new Map<string, SimNPC[]>();
  for (const npc of npcs) {
    if (!npc.group) continue;
    if (!groupNpcs.has(npc.group)) {
      groupNpcs.set(npc.group, []);
    }
    groupNpcs.get(npc.group)!.push(npc);
  }

  const groupIds = Array.from(groupNpcs.keys());

  // === 1. 内部戦闘（internalBattle: true のグループ） ===
  for (const groupId of groupIds) {
    const groupConfig = world.groupModeConfig?.groups.find(g => g.id === groupId);
    if (!groupConfig) continue;

    const battleConfig = getGroupBattleConfig(groupConfig);
    if (!battleConfig.internalBattle) continue;

    const members = groupNpcs.get(groupId) || [];
    if (members.length < 2) continue;

    // グループ内でペアリング
    const shuffled = [...members].sort(() => Math.random() - 0.5);
    const internalPairs: DuelPair[] = [];

    for (let i = 0; i + 1 < shuffled.length; i += 2) {
      internalPairs.push({ npc1: shuffled[i], npc2: shuffled[i + 1] });
    }

    // 内部戦闘を実行
    for (const { npc1, npc2 } of internalPairs) {
      executeDuel(world, npc1, npc2, 'internal');
    }
  }

  // === 2. 外部戦闘（externalBattle: true のグループ同士） ===
  // 外部戦闘を行うグループのNPCを収集
  const externalGroupNpcs = new Map<string, SimNPC[]>();
  for (const groupId of groupIds) {
    const groupConfig = world.groupModeConfig?.groups.find(g => g.id === groupId);
    if (!groupConfig) continue;

    const battleConfig = getGroupBattleConfig(groupConfig);
    if (!battleConfig.externalBattle) continue;

    externalGroupNpcs.set(groupId, [...(groupNpcs.get(groupId) || [])]);
  }

  const externalGroupIds = Array.from(externalGroupNpcs.keys());
  if (externalGroupIds.length < 2) return;

  const matched = new Set<string>();

  // ラウンドロビン方式でペアリング
  for (let round = 0; round < 3; round++) {
    for (let i = 0; i < externalGroupIds.length; i++) {
      const groupA = externalGroupIds[i];
      const groupB = externalGroupIds[(i + 1 + round) % externalGroupIds.length];
      if (groupA === groupB) continue;

      const npcListA = (externalGroupNpcs.get(groupA) || []).filter(n => !matched.has(n.id));
      const npcListB = (externalGroupNpcs.get(groupB) || []).filter(n => !matched.has(n.id));

      const pairCount = Math.min(npcListA.length, npcListB.length);

      const shuffledA = [...npcListA].sort(() => Math.random() - 0.5);
      const shuffledB = [...npcListB].sort(() => Math.random() - 0.5);

      for (let j = 0; j < pairCount; j++) {
        const npc1 = shuffledA[j];
        const npc2 = shuffledB[j];
        executeDuel(world, npc1, npc2, 'external');
        matched.add(npc1.id);
        matched.add(npc2.id);
      }
    }
  }
}

/**
 * 戦闘タイプ
 */
type DuelType = 'normal' | 'internal' | 'external';

/**
 * デュエル実行
 * @param duelType 戦闘タイプ（normal: 通常, internal: グループ内, external: グループ外）
 */
function executeDuel(world: SimWorld, npc1: SimNPC, npc2: SimNPC, duelType: DuelType = 'normal'): void {
  // デッキをシャッフル（毎回異なる初期手札になるように）
  const shuffledDeck1 = shuffle(npc1.deck);
  const shuffledDeck2 = shuffle(npc2.deck);

  // 既存のSimulator.tsを使用してデュエル実行
  const result = simulateGame({
    deck1: shuffledDeck1,
    deck2: shuffledDeck2,
    p1Difficulty: 'evolved',
    p2Difficulty: 'evolved',
    summoningSickness: false,   // 召喚酔いなし
    secondPlayerBonusSP: false, // 後攻ボーナスSPなし
    piercingDamage: false,      // 貫通ダメージなし
  });

  const winner = result.winner === 1 ? npc1 : npc2;
  const loser = result.winner === 1 ? npc2 : npc1;

  // グローバル統計に記録
  recordDuelResult(world, winner, loser);

  // Eloレーティング更新
  const { winnerNewRating, loserNewRating } = calculateEloRating(
    winner.rating,
    loser.rating
  );
  const winnerChange = winnerNewRating - winner.rating;
  const loserChange = loserNewRating - loser.rating;
  winner.rating = winnerNewRating;
  loser.rating = loserNewRating;
  winner.wins++;
  loser.losses++;

  // === 非対称進化モード: 淘汰判定用の記録 ===
  // duelTypeに応じて recordedWins/recordedLosses を更新
  if (duelType !== 'normal') {
    const winnerBattleConfig = getBattleConfigForNpc(winner, world);
    const loserBattleConfig = getBattleConfigForNpc(loser, world);

    // 勝者の記録判定
    const shouldRecordWinner = duelType === 'internal'
      ? winnerBattleConfig?.recordInternal
      : winnerBattleConfig?.recordExternal;

    // 敗者の記録判定
    const shouldRecordLoser = duelType === 'internal'
      ? loserBattleConfig?.recordInternal
      : loserBattleConfig?.recordExternal;

    if (shouldRecordWinner) {
      winner.recordedWins++;
    }
    if (shouldRecordLoser) {
      loser.recordedLosses++;
    }
  }

  // 対戦履歴を更新
  updateRecentOpponents(npc1, npc2.id);
  updateRecentOpponents(npc2, npc1.id);

  // デッキ内全カードの gamesInDeck を更新（試合に参加した時点でカウント）
  updateGamesInDeckForAllCards(npc1);
  updateGamesInDeckForAllCards(npc2);

  // カード別勝率統計を更新（実際に使用したカードのみ）
  const finalState = result.finalState;
  if (finalState) {
    // playedCardsHistoryから使用カードを取得（召喚・発動時点で記録されたもの）
    const p1UsedCards = getUsedCardIdsFromHistory(finalState.playedCardsHistory?.p1);
    const p2UsedCards = getUsedCardIdsFromHistory(finalState.playedCardsHistory?.p2);

    // 勝敗に応じて統計更新（通常カード統計）
    if (result.winner === 1) {
      updateCardStatsForUsed(npc1, p1UsedCards, true);
      updateCardStatsForUsed(npc2, p2UsedCards, false);
    } else {
      updateCardStatsForUsed(npc1, p1UsedCards, false);
      updateCardStatsForUsed(npc2, p2UsedCards, true);
    }

    // === 非対称進化モード: 淘汰判定用カード統計の記録 ===
    if (duelType !== 'normal') {
      const npc1BattleConfig = getBattleConfigForNpc(npc1, world);
      const npc2BattleConfig = getBattleConfigForNpc(npc2, world);

      const shouldRecordNpc1 = duelType === 'internal'
        ? npc1BattleConfig?.recordInternal
        : npc1BattleConfig?.recordExternal;

      const shouldRecordNpc2 = duelType === 'internal'
        ? npc2BattleConfig?.recordInternal
        : npc2BattleConfig?.recordExternal;

      // デッキ内全カードの gamesInDeck を更新（recordedCardStats用）
      if (shouldRecordNpc1) {
        updateRecordedGamesInDeckForAllCards(npc1);
        updateRecordedCardStatsForUsed(npc1, p1UsedCards, result.winner === 1);
      }
      if (shouldRecordNpc2) {
        updateRecordedGamesInDeckForAllCards(npc2);
        updateRecordedCardStatsForUsed(npc2, p2UsedCards, result.winner === 2);
      }
    }
  }

  // 関係性変化（切磋琢磨で絆が深まる）
  updateRelationship(winner, loser, 1, world.day); // 勝者→敗者: 良い戦いだった
  updateRelationship(loser, winner, 0, world.day); // 敗者→勝者: 変化なし

  addLog(
    world,
    `⚔️  ${winner.name} vs ${loser.name} → ${winner.name}勝利 ` +
      `(Rating: ${winner.rating} ${winnerChange >= 0 ? '+' : ''}${winnerChange})`
  );
}

/**
 * playedCardsHistoryから使用されたカードIDを取得
 * 召喚・魔法発動・フィールド/フェイズカード設置時点で記録されたカードのみをカウント
 * （旧方式: 墓地+フィールドからの推定 → 不正確）
 */
function getUsedCardIdsFromHistory(
  history: Array<{ id: string; action: string }> | undefined
): Set<string> {
  const usedIds = new Set<string>();

  if (!history) return usedIds;

  for (const entry of history) {
    usedIds.add(entry.id);
  }

  return usedIds;
}

/**
 * 実際に使用したカードの統計を更新
 */
function updateCardStatsForUsed(npc: SimNPC, usedCardIds: Set<string>, won: boolean): void {
  for (const cardId of usedCardIds) {
    const stat = npc.cardStats.get(cardId) || {
      gamesWithCard: 0,
      winsWithCard: 0,
      gamesInDeck: 0,
    };
    stat.gamesWithCard++;
    if (won) {
      stat.winsWithCard++;
    }
    npc.cardStats.set(cardId, stat);
  }
}

/**
 * デッキ内全カードの gamesInDeck を更新
 */
function updateGamesInDeckForAllCards(npc: SimNPC): void {
  for (const card of npc.deck) {
    const stat = npc.cardStats.get(card.id) || {
      gamesWithCard: 0,
      winsWithCard: 0,
      gamesInDeck: 0,
    };
    stat.gamesInDeck++;
    npc.cardStats.set(card.id, stat);
  }
}

/**
 * 淘汰判定用カード統計を更新（非対称進化モード用）
 */
function updateRecordedCardStatsForUsed(npc: SimNPC, usedCardIds: Set<string>, won: boolean): void {
  for (const cardId of usedCardIds) {
    const stat = npc.recordedCardStats.get(cardId) || {
      gamesWithCard: 0,
      winsWithCard: 0,
      gamesInDeck: 0,
    };
    stat.gamesWithCard++;
    if (won) {
      stat.winsWithCard++;
    }
    npc.recordedCardStats.set(cardId, stat);
  }
}

/**
 * デッキ内全カードの gamesInDeck を更新（非対称進化モード用）
 */
function updateRecordedGamesInDeckForAllCards(npc: SimNPC): void {
  for (const card of npc.deck) {
    const stat = npc.recordedCardStats.get(card.id) || {
      gamesWithCard: 0,
      winsWithCard: 0,
      gamesInDeck: 0,
    };
    stat.gamesInDeck++;
    npc.recordedCardStats.set(card.id, stat);
  }
}

/**
 * 友好的交流処理（ランダムペア）
 * デュエル以外の交流で好感度を上げる機会を提供
 * パートナーがいないNPC同士を優先的にマッチング
 * グループモードでは同グループ内でのみ交流
 */
function processPositiveInteractions(world: SimWorld): void {
  const { npcs, day } = world;
  const alive = npcs.filter((n) => n.isAlive);
  const groupModeEnabled = isGroupModeEnabled(world);

  if (groupModeEnabled) {
    // グループモード: 各グループ内で交流
    processPositiveInteractionsGroupMode(world);
    return;
  }

  // 通常モード: 従来通りの処理
  // パートナーがいないNPCを抽出
  const singles = alive.filter((n) => !n.hasPartner);

  // 人口に応じてスケール: 人口の30% + 2回（最低3回）
  const baseCount = Math.floor(alive.length * 0.3) + 2;
  const interactionCount = Math.max(3, baseCount + Math.floor(Math.random() * 3) - 1);

  let singleInteractions = 0;

  for (let i = 0; i < interactionCount && alive.length >= 2; i++) {
    let npc1: typeof alive[0];
    let npc2: typeof alive[0];

    // 70%の確率でシングル同士を優先（シングルが2人以上いる場合）
    if (singles.length >= 2 && Math.random() < 0.7) {
      const idx1 = Math.floor(Math.random() * singles.length);
      let idx2 = Math.floor(Math.random() * singles.length);
      if (idx1 === idx2) {
        idx2 = (idx2 + 1) % singles.length;
      }
      npc1 = singles[idx1];
      npc2 = singles[idx2];
      singleInteractions++;
    } else {
      // 通常のランダム交流
      const idx1 = Math.floor(Math.random() * alive.length);
      let idx2 = Math.floor(Math.random() * alive.length);
      if (idx1 === idx2) {
        idx2 = (idx2 + 1) % alive.length;
      }
      npc1 = alive[idx1];
      npc2 = alive[idx2];
    }

    // 友好的交流で好感度上昇
    const affectionGain = Math.floor(Math.random() * 10) + 8; // 8-17
    updateRelationship(npc1, npc2, affectionGain, day);

    // ログは10%の確率で表示（spam防止）
    if (Math.random() < 0.1) {
      addLog(world, `💬 ${npc1.name}と${npc2.name}が交流した`);
    }
  }

  // 交流サマリーログ
  addLog(world, `📊 本日の交流: ${interactionCount}組（独身優先: ${singleInteractions}組）`);
}

/**
 * グループモード用交流処理
 * 同グループ内でのみ交流
 */
function processPositiveInteractionsGroupMode(world: SimWorld): void {
  const { npcs, day } = world;

  // グループ別にNPCを分類
  const groupNpcs = new Map<string, SimNPC[]>();
  for (const npc of npcs) {
    if (!npc.isAlive || !npc.group) continue;
    if (!groupNpcs.has(npc.group)) {
      groupNpcs.set(npc.group, []);
    }
    groupNpcs.get(npc.group)!.push(npc);
  }

  let totalInteractions = 0;
  let totalSingleInteractions = 0;

  // 各グループ内で交流
  for (const [groupId, members] of groupNpcs) {
    if (members.length < 2) continue;

    const singles = members.filter((n) => !n.hasPartner);

    // グループ人口に応じてスケール
    const baseCount = Math.floor(members.length * 0.3) + 1;
    const interactionCount = Math.max(2, baseCount + Math.floor(Math.random() * 2));

    for (let i = 0; i < interactionCount && members.length >= 2; i++) {
      let npc1: SimNPC;
      let npc2: SimNPC;

      // 70%の確率でシングル同士を優先
      if (singles.length >= 2 && Math.random() < 0.7) {
        const idx1 = Math.floor(Math.random() * singles.length);
        let idx2 = Math.floor(Math.random() * singles.length);
        if (idx1 === idx2) {
          idx2 = (idx2 + 1) % singles.length;
        }
        npc1 = singles[idx1];
        npc2 = singles[idx2];
        totalSingleInteractions++;
      } else {
        const idx1 = Math.floor(Math.random() * members.length);
        let idx2 = Math.floor(Math.random() * members.length);
        if (idx1 === idx2) {
          idx2 = (idx2 + 1) % members.length;
        }
        npc1 = members[idx1];
        npc2 = members[idx2];
      }

      const affectionGain = Math.floor(Math.random() * 10) + 8;
      updateRelationship(npc1, npc2, affectionGain, day);
      totalInteractions++;

      if (Math.random() < 0.1) {
        addLog(world, `💬 [${groupId}] ${npc1.name}と${npc2.name}が交流した`);
      }
    }
  }

  addLog(world, `📊 本日の交流: ${totalInteractions}組（独身優先: ${totalSingleInteractions}組）`);
}
